import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { t } from "../i18n";

const PAGE_SIZE = 50;

function formatDuration(seconds) {
  if (!seconds) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function TrackSelector({
  scene,
  onPlayTrack,
  onShowDetails,
  onAddToPlaylist,
  onToggleLike,
  onToggleFavorite,
  likedTracks,
  favoritedTracks,
  onClose,
  onShowPricing,
  lang = 'zh',
}) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToPlaylist, setAddedToPlaylist] = useState(new Set());
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [downloading, setDownloading] = useState(new Set());
  const [batchDownloading, setBatchDownloading] = useState(false);
  const [showMultiSelect, setShowMultiSelect] = useState(false);
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(tracks.length / PAGE_SIZE));
  const pageTracks = tracks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function handleAddToPlaylist(track) {
    onAddToPlaylist(track);
    setAddedToPlaylist((prev) => {
      const next = new Set(prev);
      next.add(track.id);
      return next;
    });
    setTimeout(() => {
      setAddedToPlaylist((prev) => {
        const next = new Set(prev);
        next.delete(track.id);
        return next;
      });
    }, 1500);
  }

  useEffect(() => {
    async function fetchTracks() {
      try {
        setLoading(true);
        const res = await fetch(`/api/scenes/${scene.id}/tracks?limit=999`);
        if (!res.ok) throw new Error(t(lang, 'fetchFailed'));
        const data = await res.json();
        setTracks(data || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchTracks();
  }, [scene.id, lang]);

  // Toggle single track selection
  function toggleSelect(trackId) {
    setSelectedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  }

  // Select / deselect all (only on current page)
  function toggleSelectAll() {
    const pageIds = pageTracks.map((t) => t.id);
    const allSelected = pageIds.every((id) => selectedTracks.has(id));
    if (allSelected) {
      setSelectedTracks((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedTracks((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  // Single track download via API
  async function handleDownload(track) {
    const token = localStorage.getItem("fm_token");
    if (!token) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }

    setDownloading((prev) => new Set(prev).add(track.id));
    try {
      const res = await fetch(`/api/tracks/${track.id}/download`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        onShowPricing?.();
        return;
      }
      if (!res.ok) throw new Error(t(lang, 'downloadFailed'));
      const data = await res.json();
      const a = document.createElement("a");
      a.href = data.url || data.download_url;
      a.download = track.name || `track_${track.id}`;
      a.click();
    } catch (e) {
      console.error("Download error:", e);
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.delete(track.id);
        return next;
      });
    }
  }

  // Batch download via API
  async function handleBatchDownload() {
    const token = localStorage.getItem("fm_token");
    if (!token) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }

    const trackIds = Array.from(selectedTracks);
    if (trackIds.length === 0) return;

    setBatchDownloading(true);
    try {
      const res = await fetch("/api/tracks/batch-download", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ track_ids: trackIds }),
      });
      if (res.status === 403) {
        onShowPricing?.();
        return;
      }
      if (!res.ok) throw new Error(t(lang, 'downloadFailed'));
      const data = await res.json();
      const urls = data.urls || [];
      urls.forEach((url, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = url;
          a.download = `track_${trackIds[i]}`;
          a.click();
        }, i * 500);
      });
      setSelectedTracks(new Set());
    } catch (e) {
      console.error("Batch download error:", e);
    } finally {
      setBatchDownloading(false);
    }
  }

  const pageAllSelected =
    pageTracks.length > 0 &&
    pageTracks.every((t) => selectedTracks.has(t.id));

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full max-h-[80vh] flex flex-col">
      {/* Header */}
      <div
        className="h-32 bg-cover bg-center relative shrink-0"
        style={{ backgroundImage: `url(${scene.image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <button
          onClick={onClose}
          className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white hover:bg-black/60 transition"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="absolute bottom-3 left-4 right-4">
          <h2 className="text-xl font-bold text-white">{scene.name}</h2>
          <p className="text-white/70 text-sm">{scene.nameEn}</p>
        </div>
      </div>

      {/* Multi-select toggle + batch download bar */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between shrink-0 gap-2 flex-wrap">
        <button
          onClick={() => {
            setShowMultiSelect((v) => !v);
            if (showMultiSelect) setSelectedTracks(new Set());
          }}
          className={`text-xs px-3 py-1.5 rounded-lg border transition ${
            showMultiSelect
              ? "bg-indigo-600 text-white border-indigo-600"
              : "border-gray-300 text-gray-600 hover:border-indigo-400"
          }`}
        >
          {showMultiSelect ? t(lang, 'multiSelectOn') : t(lang, 'multiSelect')}
        </button>

        {showMultiSelect && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-indigo-700 font-medium">
              {t(lang, 'selectedForDownload')}: {selectedTracks.size}
            </span>
            <button
              onClick={toggleSelectAll}
              className="text-xs px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-100 transition"
            >
              {pageAllSelected ? t(lang, 'deselectAll') : t(lang, 'selectAll')}
            </button>
            <button
              onClick={handleBatchDownload}
              disabled={batchDownloading || selectedTracks.size === 0}
              className="text-xs px-4 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {batchDownloading ? t(lang, 'downloadInProgress') : t(lang, 'batchDownload')}
            </button>
          </div>
        )}
      </div>

      {/* Track list */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <div className="animate-pulse">{t(lang, 'loading')}</div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12 text-red-400">
            {error}
          </div>
        )}

        {!loading && !error && tracks.length === 0 && (
          <div className="flex items-center justify-center py-12 text-gray-400">
            {t(lang, 'noTracks')}
          </div>
        )}

        {!loading && !error && pageTracks.map((track, i) => {
          const isLiked = likedTracks?.has(track.id);
          const isFavorited = favoritedTracks?.has(track.id);
          const isSelected = selectedTracks.has(track.id);
          const isDownloading = downloading.has(track.id);
          const globalIndex = page * PAGE_SIZE + i;

          return (
            <div
              key={track.id}
              className={`flex items-center gap-2 px-4 py-3 hover:bg-indigo-50 border-b border-gray-100 last:border-0 transition ${
                isSelected ? "bg-indigo-50/60" : ""
              }`}
            >
              {/* Checkbox — only when multi-select is on */}
              {showMultiSelect && (
                <button
                  onClick={() => toggleSelect(track.id)}
                  className={`w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center transition ${
                    isSelected
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "border-gray-300 hover:border-indigo-400"
                  }`}
                >
                  {isSelected && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              )}

              {/* Number */}
              <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs shrink-0">
                {globalIndex + 1}
              </span>

              {/* Track info */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm" style={{ wordBreak: 'break-all' }}>
                  {track.name || `${t(lang, 'track')} ${globalIndex + 1}`}
                </div>
                <div className="text-xs text-gray-400">
                  {track.type === "songs" ? t(lang, 'vocal') : t(lang, 'instrumental')} · {formatDuration(track.duration_s)}
                  {track.like_count > 0 && (
                    <span className="ml-2 text-red-400">♥ {track.like_count}</span>
                  )}
                  {track.favorite_count > 0 && (
                    <span className="ml-1.5 text-amber-500">★ {track.favorite_count}</span>
                  )}
                </div>
              </div>

              {/* Action buttons group */}
              <div className="flex items-center gap-0.5 shrink-0 overflow-x-auto scrollbar-none">
                {/* 1. Play in PlayerBar */}
                <button
                  onClick={() => onPlayTrack(track, scene)}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition"
                  title={t(lang, 'clickPlay')}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>

                {/* 2. Add to Playlist */}
                <button
                  onClick={() => handleAddToPlaylist(track)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                    addedToPlaylist.has(track.id)
                      ? "text-emerald-500 bg-emerald-50 scale-110"
                      : "text-gray-400 hover:text-indigo-500 hover:bg-indigo-50"
                  }`}
                  title={t(lang, 'addToPlaylist')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {addedToPlaylist.has(track.id) ? (
                      <polyline points="20 6 9 17 4 12" />
                    ) : (
                      <>
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </>
                    )}
                  </svg>
                </button>

                {/* 3. Like */}
                <button
                  onClick={() => onToggleLike(track)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                    isLiked
                      ? "text-red-500 bg-red-50"
                      : "text-gray-400 hover:text-red-400 hover:bg-gray-100"
                  }`}
                  title={isLiked ? t(lang, 'unlike') : t(lang, 'like')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>

                {/* 4. Favorite */}
                <button
                  onClick={() => onToggleFavorite(track)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                    isFavorited
                      ? "text-amber-500 bg-amber-50"
                      : "text-gray-400 hover:text-amber-400 hover:bg-gray-100"
                  }`}
                  title={isFavorited ? t(lang, 'unfavorite') : t(lang, 'favorite')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>

                {/* 5. Download via API */}
                <button
                  onClick={() => handleDownload(track)}
                  disabled={isDownloading}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
                  title={t(lang, 'download')}
                >
                  {isDownloading ? (
                    <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                </button>

                {/* 6. Details */}
                <button
                  onClick={() => onShowDetails(track, scene)}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition"
                  title={t(lang, 'details')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </button>

                {/* 7. Open page (SEO) */}
                <Link
                  to={`/track/${track.id}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-50 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 transition"
                  title={t(lang, 'details')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-2 shrink-0 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`px-3 py-1 text-xs rounded-lg transition ${
                i === page
                  ? "bg-indigo-600 text-white"
                  : "border border-gray-300 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            →
          </button>
        </div>
      )}

      {/* Footer hint */}
      <div className="px-4 py-3 bg-white border-t border-gray-100 text-center text-xs text-gray-400 shrink-0">
        {tracks.length} {t(lang, 'tracks')}
        {totalPages > 1 && (
          <span className="ml-2 text-gray-300">
            · {t(lang, 'page')} {page + 1}/{totalPages}
          </span>
        )}
      </div>
    </div>
  );
}
